import { reconnectWithNewAuth, getConfig } from '@pooflabs/core';
import { getAuthProvider, login as authLogin, logout as authLogout, clearIncompatibleSession, getCurrentAuthMethod } from './auth';
import { init as configInit } from '@pooflabs/core';
let authProviderInstance = null;
let currentUser = null;
let authStateListeners = [];
let authLoadingListeners = [];
let initCompleted = false;
let isAuthLoading = false;
// This file acts as a middleware for the global state of the SDK
// This mostly involves setting up the AuthProvider and managing the current user
export async function init(newConfig) {
    // Get auth provider first
    authProviderInstance = await getAuthProvider(newConfig);
    // Initialize config with auth provider
    await configInit(Object.assign(Object.assign({}, newConfig), { authProvider: authProviderInstance }));
    // Now that core is initialized, update the auth method on the config
    const authMethod = getCurrentAuthMethod();
    if (authMethod) {
        const coreConfig = await getConfig();
        // Map client-only auth methods to their backend-compatible equivalents.
        // 'privy-expo' is the same Privy auth flow, just from Expo/RN instead of web.
        coreConfig.authMethod = (authMethod === 'privy-expo' ? 'privy' : authMethod);
    }
    // Wait for restoreSession to complete
    // The appId check is done in SessionManager.getSession which is called by restoreSession
    // If there's an appId mismatch, the session will be cleared and restoreSession will return null
    let user = await authProviderInstance.restoreSession();
    // If a session exists but was created before auth method tracking (legacy session),
    // clear it to avoid mismatches when the app switches auth methods (e.g. privy → phantom)
    if (user && clearIncompatibleSession()) {
        user = null;
    }
    setCurrentUser(user);
    // Mark init as completed
    initCompleted = true;
}
// TODO: Have a way to remove listeners
export function onAuthStateChanged(callback) {
    authStateListeners.push(callback);
    if (initCompleted || hasRunFirstTime) {
        // Call the callback immediately with the current user.
        // We check hasRunFirstTime in addition to initCompleted to handle the race
        // where the Phantom auto-login effect calls setCurrentUser() before init()
        // finishes. Without this, listeners registered between auto-login's
        // setCurrentUser and init()'s initCompleted would never fire (init's
        // setCurrentUser is a no-op for same-address and initCompleted is still false).
        callback(currentUser);
    }
}
export function onAuthLoadingChanged(callback) {
    authLoadingListeners.push(callback);
    // Call immediately with current loading state
    callback(isAuthLoading);
}
export function setAuthLoading(loading) {
    if (isAuthLoading !== loading) {
        isAuthLoading = loading;
        authLoadingListeners.forEach((callback) => callback(loading));
    }
}
export function getAuthLoading() {
    return isAuthLoading;
}
export async function login(options) {
    if (!authProviderInstance) {
        throw new Error('SDK not initialized. Please call init() first.');
    }
    const loggedInUser = await authLogin(options);
    setCurrentUser(loggedInUser);
    return currentUser;
}
export async function logout() {
    if (!authProviderInstance) {
        throw new Error('SDK not initialized. Please call init() first.');
    }
    await authLogout();
    setCurrentUser(null);
}
var hasRunFirstTime = false;
// Update the currentUser and notify listeners
export function setCurrentUser(user) {
    const lastUser = currentUser;
    const wasInitializedBefore = hasRunFirstTime;
    const shouldRunListeners = !hasRunFirstTime || (currentUser === null || currentUser === void 0 ? void 0 : currentUser.address) !== (user === null || user === void 0 ? void 0 : user.address);
    hasRunFirstTime = true;
    currentUser = user;
    if (shouldRunListeners) {
        authStateListeners.forEach((callback) => callback(user));
        // Reconnect WebSocket connections with new auth when user changes.
        // Skip on the first setCurrentUser call during init() — there are no
        // existing connections to reconnect at that point.
        if (wasInitializedBefore && (lastUser === null || lastUser === void 0 ? void 0 : lastUser.address) !== (user === null || user === void 0 ? void 0 : user.address)) {
            reconnectWithNewAuth().catch((error) => {
                console.error('Error reconnecting WebSocket after auth change:', error);
            });
        }
    }
}
export function getCurrentUser() {
    return currentUser;
}
export function setAuthProviderInstance(provider) {
    authProviderInstance = provider;
}
